import { default as Alert } from 'antd/es/alert';
import { default as Modal } from 'antd/es/modal';
import type { Dispatch } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import { ErrorBoundary } from '@templates/ErrorBoundary';
import { usePostVirtualBankInterAccountMovements } from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { TransactionTwoWayIcon } from '@atoms/Icon';

import { AmountInput } from '@molecules/AmountInput';
import { InputField } from '@molecules/InputField';
import { LabelledInput } from '@molecules/LabelledInput';
import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import { TransferFXDetails } from './TransferFXDetails';

import {
  INTER_TRANSFER_AMOUNT_INPUT_TEST_ID,
  INTER_TRANSFER_BUTTON_PROCEED_TEST_ID,
  INTER_TRANSFER_END_TO_END_ID_INPUT_TEST_ID,
  INTER_TRANSFER_FROM_SELECTOR_TEST_ID,
  INTER_TRANSFER_TO_SELECTOR_TEST_ID
} from '../constants';

type TransferForm = {
  from_account_number: string;
  to_account_number: string;
  end_to_end_id: string;
  amount: string;
};

type Props = {
  open: boolean;
  setOpen: Dispatch<boolean>;
  defaultFromAccountNumber?: string;
};

/**
 * Dialog for handling inter account movements
 */
export const TransferDialog = ({
  open,
  setOpen,
  defaultFromAccountNumber
}: Props) => {
  const organisationId = useOrganisationId<number>();
  const openNotification = useOpenNotification();

  const defaultValues = useMemo<DefaultValues<TransferForm>>(
    () => ({
      from_account_number: defaultFromAccountNumber,
      to_account_number: undefined,
      amount: undefined,
      end_to_end_id: ''
    }),
    [defaultFromAccountNumber]
  );

  const {
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    setError,
    clearErrors
  } = useForm<TransferForm>({ defaultValues });

  useEffect(() => {
    if (open) reset(defaultValues);
  }, [open, defaultValues, reset]);

  const [isFXConfigured, setIsFXConfigured] = useState<boolean>(true);

  const { accounts, findAccountByAccountNumber } = useVirtualAccounts({
    filterUnownedAccounts: true,
    filterTerminatedAccounts: true,
    filterSuspendedAccounts: true,
    filterCryptoAccounts: true
  });

  const fromAccountNumber = watch(
    'from_account_number',
    defaultFromAccountNumber
  );
  const fromAccount = useMemo(
    () => findAccountByAccountNumber(fromAccountNumber),
    [findAccountByAccountNumber, fromAccountNumber]
  );

  const toAccounts = useMemo(
    () =>
      accounts.filter(
        (account) => account.account_number !== fromAccountNumber
      ),
    [accounts, fromAccountNumber]
  );

  const toAccountNumber = watch('to_account_number', undefined);
  const toAccount = useMemo(
    () => findAccountByAccountNumber(toAccountNumber),
    [findAccountByAccountNumber, toAccountNumber]
  );
  const isFX = useMemo(
    () =>
      fromAccount &&
      toAccount &&
      fromAccount?.currency_name !== toAccount?.currency_name,
    [fromAccount, toAccount]
  );

  const fromAmount = watch('amount', '0');

  const {
    isPending: isLoading,
    mutateAsync: createInterAccountMovement,
    error
  } = usePostVirtualBankInterAccountMovements();

  const onClose = useCallback(() => {
    reset();
    setOpen(false);
  }, [reset, setOpen]);

  const onSubmit = useCallback(
    (interAccountMovement: TransferForm) => {
      createInterAccountMovement({
        data: interAccountMovement,
        organisationId
      })
        .then(() => {
          openNotification({
            kind: 'success',
            message: 'Transfer successful',
            key: 'transfer-success'
          });
          onClose();
        })
        .catch(() => {
          openNotification({
            kind: 'error',
            message: 'Transfer failed',
            key: 'transfer-failed'
          });
        });
    },
    [createInterAccountMovement, onClose, openNotification, organisationId]
  );

  useEffect(() => {
    const details = error?.error?.details;

    if (!details) return; // Exit early if details is undefined or null

    if (typeof details === 'string') {
      // If details is a string, set it as the root error
      setError('root', { message: details });
    } else if (typeof details === 'object') {
      // If details is an object, check specific fields
      if (details['debit_amount']) {
        setError('amount', {
          message: details['debit_amount']?.[0]
        });
      }

      if (details['credit_account_number']) {
        setError('from_account_number', {
          message: details['credit_account_number']?.[0]
        });
      }

      if (details) {
        setError('root', { message: details?.toString() });
      }
    }
  }, [error, setError]);

  const formValues = watch();
  const canProceedToTransfer = useMemo(
    () => Object.values(formValues).every(Boolean) && isFXConfigured,
    [formValues, isFXConfigured]
  );

  return (
    <Modal
      destroyOnHidden
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center gap-3 text-xl">
          <TransactionTwoWayIcon
            className="size-6 stroke-current text-black"
            strokeWidth="1.0"
          />
          <div>Transfer</div>
        </div>
      }
      styles={{
        content: {
          width: 440,
          paddingTop: 12,
          backgroundColor: 'rgb(255, 255, 255)'
        }
      }}
      footer={() => (
        <>
          <PrimaryButton
            className="w-full"
            form="transferForm"
            data-testid={INTER_TRANSFER_BUTTON_PROCEED_TEST_ID}
            key="submit"
            htmlType="submit"
            disabled={isLoading || !canProceedToTransfer}
          >
            Proceed to transfer
          </PrimaryButton>
        </>
      )}
    >
      <form
        onChange={() => clearErrors()}
        className="mt-3 flex flex-col gap-3"
        onSubmit={handleSubmit(onSubmit)}
        id="transferForm"
      >
        <LabelledInput
          label="From"
          errorMessage={errors.from_account_number?.message}
        >
          <SelectVirtualAccount
            onChangeCallback={(from) => setValue('from_account_number', from)}
            className="!w-full"
            allowAll={false}
            defaultValue={defaultFromAccountNumber}
            data-testid={INTER_TRANSFER_FROM_SELECTOR_TEST_ID}
            filterUnownedAccounts={true}
            filterTerminatedAccounts={true}
            filterSuspendedAccounts={true}
            filterCryptoAccounts={true}
          />
        </LabelledInput>
        <LabelledInput label="To">
          <SelectVirtualAccount
            onChangeCallback={(to) => setValue('to_account_number', to)}
            className="!w-full"
            allowAll={false}
            accounts={toAccounts}
            disabled={fromAccount === undefined}
            data-testid={INTER_TRANSFER_TO_SELECTOR_TEST_ID}
            filterUnownedAccounts={true}
            filterTerminatedAccounts={true}
            filterSuspendedAccounts={true}
            filterCryptoAccounts={true}
          />
        </LabelledInput>
        <LabelledInput label="Amount" errorMessage={errors.amount?.message}>
          <AmountInput
            status={errors?.amount ? 'error' : undefined}
            currency={fromAccount?.currency_code}
            onChange={(amount) => setValue('amount', amount)}
            disabled={toAccount === undefined}
            data-testid={INTER_TRANSFER_AMOUNT_INPUT_TEST_ID}
            placeholder="000,000,000.00"
          />
        </LabelledInput>
        <LabelledInput label="End-to-end-ID">
          <InputField
            onChange={(event) => setValue('end_to_end_id', event.target.value)}
            disabled={toAccount === undefined}
            data-testid={INTER_TRANSFER_END_TO_END_ID_INPUT_TEST_ID}
            placeholder="My chosen end-to-end ID"
          />
        </LabelledInput>
      </form>
      {isFX && (
        <ErrorBoundary
          key={fromAmount}
          fallback={
            <Alert
              message="Unable to show FX information"
              type="error"
              className="mt-3"
            />
          }
          onErrorCatch={() => setIsFXConfigured(false)}
        >
          <TransferFXDetails
            payCurrencyCode={fromAccount?.currency_code}
            buyCurrencyCode={toAccount?.currency_code}
            amountToConvert={fromAmount}
            setIsFXConfigured={setIsFXConfigured}
            className="m-2"
          />
        </ErrorBoundary>
      )}
    </Modal>
  );
};
